import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { AlarmOn, Business, Clear, FilterList, People, Person, Public, Search } from '@mui/icons-material';
import { CONTACT_STATUS_OPTIONS, STATUS_COLORS } from '../../Components/ContactForm';
import { LEAD_TEMPERATURE_OPTIONS, READINESS, getFollowUpState } from '../../Components/ContactActivity';
import {
  DEFAULT_FILTERS,
  FOLLOW_UP_FILTERS,
  NO_STATUS,
  contactCity,
  contactIndustry,
  contactTags,
  countActiveFilters,
  countPanelFilters,
  facetOptions,
  matchesFilters,
} from './contactFilters';

const COUNTRY_FILTERS = ['India', 'United States', 'United Kingdom', 'Canada', 'Australia', 'Germany', 'France', 'United Arab Emirates', 'Singapore'];

const TYPE_OPTIONS = [
  { value: 'all', label: 'All', icon: <People sx={{ fontSize: 18 }} /> },
  { value: 'person', label: 'People', icon: <Person sx={{ fontSize: 18 }} /> },
  { value: 'company', label: 'Companies', icon: <Business sx={{ fontSize: 18 }} /> },
];

const toggle = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

const CountBadge = ({ count, active }) => (
  <Box
    component="span"
    sx={{
      ml: { xs: 0.25, sm: 0.75 },
      px: { xs: 0.5, sm: 0.75 },
      minWidth: 18,
      borderRadius: 999,
      fontSize: '0.7rem',
      fontWeight: 700,
      lineHeight: '18px',
      textAlign: 'center',
      bgcolor: active ? 'rgba(255,255,255,0.25)' : '#f1f5f9',
      color: active ? 'inherit' : '#64748b',
    }}
  >
    {count}
  </Box>
);

// Multi-select with per-option counts, used inside the "More filters" panel
const FacetSelect = ({ label, placeholder, options, value, onChange }) => (
  <Autocomplete
    multiple
    size="small"
    disableCloseOnSelect
    options={options.map((o) => o.value)}
    value={value}
    onChange={(e, next) => onChange(next)}
    noOptionsText="Nothing to filter on yet"
    renderOption={(props, option, { selected }) => {
      const count = options.find((o) => o.value === option)?.count ?? 0;
      return (
        <li {...props} key={option}>
          <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
          <Box sx={{ flex: 1 }}>{option}</Box>
          <Typography variant="caption" sx={{ color: '#94a3b8', ml: 1 }}>
            {count}
          </Typography>
        </li>
      );
    }}
    renderTags={(selected, getTagProps) =>
      selected.map((option, index) => <Chip {...getTagProps({ index })} key={option} label={option} size="small" />)
    }
    renderInput={(params) => <TextField {...params} label={label} placeholder={value.length ? '' : placeholder} />}
  />
);

const ContactFilterBar = ({ contacts, filters, onChange, country, onCountryChange, resultCount }) => {
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const [panelAnchor, setPanelAnchor] = useState(null);
  const searchRef = useRef(null);

  const update = (patch) => onChange({ ...filters, ...patch });

  // Debounce typing so the table doesn't re-filter on every keystroke
  useEffect(() => {
    if (searchDraft === filters.search) return undefined;
    const timer = setTimeout(() => onChange({ ...filters, search: searchDraft }), 200);
    return () => clearTimeout(timer);
  }, [searchDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the box in sync when filters are cleared from outside (chips, Clear all)
  useEffect(() => setSearchDraft(filters.search), [filters.search]);

  // Press "/" anywhere to jump to search
  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target.tagName;
      if (event.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !event.target.isContentEditable) {
        event.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const typeCounts = useMemo(() => {
    const pool = contacts.filter((c) => matchesFilters(c, filters, 'type'));
    return {
      all: pool.length,
      person: pool.filter((c) => c.contactType === 'person').length,
      company: pool.filter((c) => c.contactType === 'company').length,
    };
  }, [contacts, filters]);

  const statusCounts = useMemo(() => {
    const counts = {};
    contacts
      .filter((c) => matchesFilters(c, filters, 'statuses'))
      .forEach((c) => {
        const key = c.contactStatus || NO_STATUS;
        counts[key] = (counts[key] || 0) + 1;
      });
    return counts;
  }, [contacts, filters]);

  const cityOptions = useMemo(() => facetOptions(contacts, filters, 'cities', contactCity), [contacts, filters]);
  const tagOptions = useMemo(() => facetOptions(contacts, filters, 'tags', contactTags), [contacts, filters]);
  const sourceOptions = useMemo(() => facetOptions(contacts, filters, 'sources', (c) => c.leadSource), [contacts, filters]);
  const industryOptions = useMemo(() => facetOptions(contacts, filters, 'industries', contactIndustry), [contacts, filters]);

  const dueCount = useMemo(
    () =>
      contacts.filter((c) => {
        const state = getFollowUpState(c.nextFollowUpAt);
        return state && state.diffDays <= 0 && matchesFilters(c, filters, 'followUp');
      }).length,
    [contacts, filters]
  );

  const activeCount = countActiveFilters(filters);
  const panelCount = countPanelFilters(filters);

  // Status chips: known statuses plus "Not set"; hide zero-count ones unless selected
  const statusChips = [...CONTACT_STATUS_OPTIONS, NO_STATUS]
    .map((value) => ({ value, count: statusCounts[value] || 0, label: value === NO_STATUS ? 'Not set' : value }))
    .filter((s) => s.count > 0 || filters.statuses.includes(s.value));

  // One removable chip per active filter
  const activeChips = [
    filters.search.trim() && { key: 'search', label: `“${filters.search.trim()}”`, onDelete: () => update({ search: '' }) },
    filters.type !== 'all' && { key: 'type', label: filters.type === 'person' ? 'People' : 'Companies', onDelete: () => update({ type: 'all' }) },
    ...filters.statuses.map((s) => ({ key: `status-${s}`, label: `Status: ${s === NO_STATUS ? 'Not set' : s}`, onDelete: () => update({ statuses: toggle(filters.statuses, s) }) })),
    ...filters.cities.map((c) => ({ key: `city-${c}`, label: `City: ${c}`, onDelete: () => update({ cities: toggle(filters.cities, c) }) })),
    ...filters.tags.map((t) => ({ key: `tag-${t}`, label: `Tag: ${t}`, onDelete: () => update({ tags: toggle(filters.tags, t) }) })),
    ...filters.sources.map((s) => ({ key: `source-${s}`, label: `Source: ${s}`, onDelete: () => update({ sources: toggle(filters.sources, s) }) })),
    ...filters.industries.map((i) => ({ key: `industry-${i}`, label: `Industry: ${i}`, onDelete: () => update({ industries: toggle(filters.industries, i) }) })),
    filters.hasEmail && { key: 'hasEmail', label: 'Has email', onDelete: () => update({ hasEmail: false }) },
    filters.hasPhone && { key: 'hasPhone', label: 'Has phone', onDelete: () => update({ hasPhone: false }) },
    filters.hasWhatsapp && { key: 'hasWhatsapp', label: 'Has WhatsApp', onDelete: () => update({ hasWhatsapp: false }) },
    filters.hasWebsite && { key: 'hasWebsite', label: 'Has website', onDelete: () => update({ hasWebsite: false }) },
    ...filters.readiness.map((r) => ({ key: `ready-${r}`, label: `Marketing: ${READINESS[r]?.short || r}`, onDelete: () => update({ readiness: toggle(filters.readiness, r) }) })),
    ...filters.temperatures.map((t) => ({ key: `temp-${t}`, label: `Lead: ${LEAD_TEMPERATURE_OPTIONS.find((o) => o.value === t)?.label || t}`, onDelete: () => update({ temperatures: toggle(filters.temperatures, t) }) })),
    filters.followUp && { key: 'followUp', label: `Follow-up: ${FOLLOW_UP_FILTERS.find((f) => f.value === filters.followUp)?.label || filters.followUp}`, onDelete: () => update({ followUp: '' }) },
  ].filter(Boolean);

  const clearAll = () => onChange({ ...DEFAULT_FILTERS });

  return (
    <Box sx={{ mb: 2 }}>
      {/* Row 1: search · type · more filters · country */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
        <TextField
          inputRef={searchRef}
          size="small"
          placeholder="Search name, email, phone, city, tag…"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && setSearchDraft('')}
          sx={{ flex: 1, minWidth: { md: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: '#94a3b8' }} />
              </InputAdornment>
            ),
            endAdornment: searchDraft ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchDraft('')} aria-label="Clear search">
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end" sx={{ display: { xs: 'none', md: 'flex' } }}>
                <Box component="kbd" sx={{ fontSize: '0.7rem', color: '#94a3b8', bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: 0.75, px: 0.75, py: 0, lineHeight: '18px', fontFamily: 'inherit', boxShadow: 'none' }}
                  title="Press / to search">
                  /
                </Box>
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          exclusive
          size="small"
          value={filters.type}
          onChange={(e, value) => value && update({ type: value })}
          aria-label="Contact type"
          sx={{
            flexShrink: 0,
            width: { xs: '100%', md: 'auto' },
            '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, px: { xs: 0.5, sm: 1.5 }, gap: 0.5, minWidth: 0, color: '#475569', borderColor: '#e2e8f0', whiteSpace: 'nowrap' },
            '& .type-icon': { display: { xs: 'none', sm: 'inline-flex' } },
            '& .Mui-selected': { bgcolor: '#3b82f6 !important', color: '#fff !important' },
          }}
        >
          {TYPE_OPTIONS.map((option) => (
            <ToggleButton key={option.value} value={option.value} sx={{ flex: { xs: 1, md: 'none' } }}>
              <Box component="span" className="type-icon">{option.icon}</Box>
              {option.label}
              <CountBadge count={typeCounts[option.value]} active={filters.type === option.value} />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Stack direction="row" spacing={1.5} sx={{ flexShrink: 0, minWidth: 0 }}>
          <Badge badgeContent={panelCount} color="primary" sx={{ flex: { xs: 1, md: 'none' } }}>
            <Button
              variant={panelCount ? 'contained' : 'outlined'}
              disableElevation
              startIcon={<FilterList />}
              onClick={(e) => setPanelAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                width: '100%',
                height: 40,
                whiteSpace: 'nowrap',
                ...(panelCount
                  ? { bgcolor: '#eff6ff', color: '#1d4ed8', '&:hover': { bgcolor: '#dbeafe' } }
                  : { borderColor: '#e2e8f0', color: '#475569' }),
              }}
            >
              More filters
            </Button>
          </Badge>

          <TextField
            select
            size="small"
            value={country}
            onChange={(e) => onCountryChange(e.target.value)}
            sx={{ minWidth: { xs: 0, md: 170 }, flex: { xs: 1, md: 'none' } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Public sx={{ fontSize: 18, color: '#94a3b8' }} />
                </InputAdornment>
              ),
            }}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
            aria-label="Country"
          >
            {COUNTRY_FILTERS.map((c) => (
              <MenuItem key={c} value={c}>{c}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Stack>

      {/* Row 2: status quick filters */}
      {(statusChips.length > 0 || dueCount > 0 || filters.followUp === 'due') && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          {(dueCount > 0 || filters.followUp === 'due') && (
            <>
              <Chip
                clickable
                size="small"
                icon={<AlarmOn sx={{ fontSize: '16px !important', color: '#ef4444 !important' }} />}
                label={
                  <span>
                    Follow-ups due
                    <Box component="span" sx={{ ml: 0.75, fontWeight: 700 }}>{dueCount}</Box>
                  </span>
                }
                aria-pressed={filters.followUp === 'due'}
                onClick={() => update({ followUp: filters.followUp === 'due' ? '' : 'due' })}
                sx={{
                  fontWeight: 600,
                  bgcolor: filters.followUp === 'due' ? '#fee2e2' : '#fef2f2',
                  color: '#b91c1c',
                  border: `1px solid ${filters.followUp === 'due' ? '#ef4444' : '#fecaca'}`,
                }}
              />
              <Box sx={{ width: '1px', height: 20, bgcolor: '#e2e8f0' }} />
            </>
          )}
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mr: 0.5 }}>
            Status
          </Typography>
          {statusChips.map((status) => {
            const selected = filters.statuses.includes(status.value);
            const color = STATUS_COLORS[status.value] || '#94a3b8';
            return (
              <Chip
                key={status.value}
                clickable
                onClick={() => update({ statuses: toggle(filters.statuses, status.value) })}
                variant={selected ? 'filled' : 'outlined'}
                size="small"
                aria-pressed={selected}
                icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color, ml: '8px !important' }} />}
                label={
                  <span>
                    {status.label}
                    <Box component="span" sx={{ ml: 0.75, color: selected ? '#1e293b' : '#94a3b8', fontWeight: 700 }}>
                      {status.count}
                    </Box>
                  </span>
                }
                sx={{
                  fontWeight: 600,
                  borderColor: selected ? color : '#e2e8f0',
                  bgcolor: selected ? `${color}22` : '#fff',
                  color: '#334155',
                  '&:hover': { bgcolor: `${color}14 !important` },
                }}
              />
            );
          })}
        </Stack>
      )}

      {/* Row 3: active filters summary */}
      {activeCount > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1.5, p: 1, pl: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #f1f5f9', flexWrap: 'wrap', rowGap: 1 }}
        >
          <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mr: 0.5 }}>
            {resultCount} result{resultCount === 1 ? '' : 's'}
          </Typography>
          {activeChips.map((chip) => (
            <Chip key={chip.key} label={chip.label} size="small" onDelete={chip.onDelete} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', fontWeight: 500 }} />
          ))}
          <Button size="small" onClick={clearAll} sx={{ textTransform: 'none', fontWeight: 600, ml: 'auto !important' }}>
            Clear all
          </Button>
        </Stack>
      )}

      {/* More filters panel */}
      <Popover
        open={Boolean(panelAnchor)}
        anchorEl={panelAnchor}
        onClose={() => setPanelAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { mt: 1, width: 360, maxWidth: 'calc(100vw - 32px)', borderRadius: 2, boxShadow: '0 12px 32px rgba(15,23,42,0.16)' } }}
      >
        <Box sx={{ p: 2, maxHeight: '65vh', overflowY: 'auto' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
            More filters
          </Typography>
          <Stack spacing={2}>
            <FacetSelect label="City" placeholder="Any city" options={cityOptions} value={filters.cities} onChange={(cities) => update({ cities })} />
            <FacetSelect label="Tags" placeholder="Any tag" options={tagOptions} value={filters.tags} onChange={(tags) => update({ tags })} />
            <FacetSelect label="Industry" placeholder="Any industry" options={industryOptions} value={filters.industries} onChange={(industries) => update({ industries })} />
            <FacetSelect label="Source" placeholder="Any source" options={sourceOptions} value={filters.sources} onChange={(sources) => update({ sources })} />
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 600, mt: 2, mb: 0.75 }}>
            Marketing readiness
          </Typography>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.75 }}>
            {Object.values(READINESS).map((r) => {
              const selected = filters.readiness.includes(r.value);
              return (
                <Chip
                  key={r.value}
                  size="small"
                  clickable
                  label={r.short}
                  aria-pressed={selected}
                  onClick={() => update({ readiness: toggle(filters.readiness, r.value) })}
                  icon={<Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color, ml: '8px !important' }} />}
                  sx={{ fontWeight: 600, bgcolor: selected ? `${r.color}22` : '#fff', border: `1px solid ${selected ? r.color : '#e2e8f0'}` }}
                />
              );
            })}
          </Stack>
          <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 600, mt: 2, mb: 0.75 }}>
            Lead temperature
          </Typography>
          <Stack direction="row" spacing={0.75}>
            {LEAD_TEMPERATURE_OPTIONS.map((t) => {
              const selected = filters.temperatures.includes(t.value);
              return (
                <Chip
                  key={t.value}
                  size="small"
                  clickable
                  label={t.label}
                  aria-pressed={selected}
                  onClick={() => update({ temperatures: toggle(filters.temperatures, t.value) })}
                  sx={{ fontWeight: 600, bgcolor: selected ? `${t.color}22` : '#fff', border: `1px solid ${selected ? t.color : '#e2e8f0'}` }}
                />
              );
            })}
          </Stack>
          <TextField
            select
            fullWidth
            size="small"
            label="Follow-up"
            value={filters.followUp}
            onChange={(e) => update({ followUp: e.target.value })}
            sx={{ mt: 2 }}
          >
            <MenuItem value=""><em>Any</em></MenuItem>
            {FOLLOW_UP_FILTERS.map((f) => (
              <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
            ))}
          </TextField>
          <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontWeight: 600, mt: 2, mb: 0.5 }}>
            Only show contacts with
          </Typography>
          <Stack direction="row" flexWrap="wrap">
            {[
              ['hasEmail', 'Email'],
              ['hasPhone', 'Phone'],
              ['hasWhatsapp', 'WhatsApp'],
              ['hasWebsite', 'Website'],
            ].map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={<Checkbox size="small" checked={filters[key]} onChange={(e) => update({ [key]: e.target.checked })} />}
                label={<Typography variant="body2">{label}</Typography>}
              />
            ))}
          </Stack>
        </Box>
        <Divider />
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.25 }}>
          <Button
            size="small"
            disabled={!panelCount}
            onClick={() => update({ cities: [], tags: [], sources: [], industries: [], hasEmail: false, hasPhone: false, hasWhatsapp: false, hasWebsite: false, readiness: [], temperatures: [], followUp: '' })}
            sx={{ textTransform: 'none' }}
          >
            Reset
          </Button>
          <Button size="small" variant="contained" disableElevation onClick={() => setPanelAnchor(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Show {resultCount} result{resultCount === 1 ? '' : 's'}
          </Button>
        </Stack>
      </Popover>
    </Box>
  );
};

export default ContactFilterBar;
